import React, { useState } from 'react';
import {
  ActionIcon,
  Divider,
  Stack,
  Title,
  Text,
  Group,
  Skeleton,
} from '@mantine/core';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import { IconCheck, IconEdit, IconX } from '@tabler/icons-react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { useGetPatientProgram } from '../../../global/entities/PatientProgram/queries/useGetPatientProgram.tsx';
import { useSetPatientProgramEventDate } from '../../../global/entities/PatientProgram/mutations/useSetPatientProgramEventDate.tsx';
import Utils from '../../../global/helpers/utils.ts';

interface IPatientProgramInfoWidgetProps {
  patientStore?: PatientStore;
}

// The backend stores the event date as a UTC-anchored calendar day. Reopen it
// as a local Date so the calendar prefills on the day the clinician set, and
// the save path (local getters) round-trips it unchanged in every timezone.
const toLocalCalendarDay = (date: Date): Date | null => {
  const formatted = Utils.formatDateToYYYYMMDD(date);
  if (!formatted) return null;
  const [year, month, day] = formatted.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const PatientProgramInfoWidget: React.FC<IPatientProgramInfoWidgetProps> =
  inject('patientStore')(
    observer(({ patientStore }) => {
      const { t } = useTranslation();
      const { data: program, isLoading, isError } = useGetPatientProgram(
        patientStore?.patientId,
      );
      const setEventDate = useSetPatientProgramEventDate(
        patientStore?.patientId,
      );
      const [isEditingDate, setIsEditingDate] = useState(false);
      const [eventDateValue, setEventDateValue] = useState<Date | null>(null);

      if (isLoading) {
        return (
          <>
            <Divider my="sm" />
            <Stack gap="xs">
              <Skeleton height={24} width="60%" />
              <Skeleton height={20} />
              <Skeleton height={20} />
            </Stack>
          </>
        );
      }

      if (isError) {
        return (
          <>
            <Divider my="sm" />
            <Text size="sm" c="dimmed">
              {t('program.loadError')}
            </Text>
          </>
        );
      }

      if (!program) return null;

      const planName =
        i18n.language === 'fr'
          ? program.carePlanNameFr
          : program.carePlanNameEn;

      const eventLabel =
        (i18n.language === 'fr'
          ? program.eventLabelFr
          : program.eventLabelEn) || t('program.eventDate');

      const handleEditDate = () => {
        setEventDateValue(
          program.eventDate ? toLocalCalendarDay(program.eventDate) : null,
        );
        setIsEditingDate(true);
      };

      const handleSaveEventDate = () => {
        if (!eventDateValue) return;
        setEventDate.mutate(eventDateValue, {
          onSuccess: () => setIsEditingDate(false),
        });
      };

      const handleCancelEdit = () => {
        setIsEditingDate(false);
      };

      return (
        <>
          <Divider my="sm" />
          <Stack gap="sm">
            <Title order={4}>{planName}</Title>
            <Group gap="xs">
              <Text size="sm" fw={600} c="dimmed">
                {eventLabel}:
              </Text>
              {isEditingDate ? (
                <Group gap={4} wrap="nowrap">
                  <CalendarDateInput
                    size="xs"
                    placeholder={t('placeHolder.dateFormat')}
                    value={eventDateValue}
                    onChange={(value) =>
                      setEventDateValue(value ? new Date(value) : null)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEventDate();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    autoFocus
                  />
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="green"
                    aria-label={t('program.saveDate')}
                    onClick={handleSaveEventDate}
                    disabled={!eventDateValue}
                    loading={setEventDate.isPending}
                  >
                    <IconCheck size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    aria-label={t('program.cancelDate')}
                    onClick={handleCancelEdit}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              ) : (
                <Group gap={4} wrap="nowrap">
                  <Text size="sm">
                    {program.eventDate
                      ? Utils.formatDateToYYYYMMDD(program.eventDate)
                      : t('program.notSet')}
                  </Text>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    aria-label={t('program.changeDate')}
                    onClick={handleEditDate}
                  >
                    <IconEdit size={14} />
                  </ActionIcon>
                </Group>
              )}
            </Group>
          </Stack>
        </>
      );
    }),
  );

export default PatientProgramInfoWidget;
